import * as T from 'three';
import { Component } from './live-codec.js';
import { readResource, resourceValue, resourceProperties, resourceCoordinate } from './vgui-res.js';

const classes = ['Player', 'Scout', 'Sniper', 'Soldier', 'Demoman', 'Medic', 'Heavy', 'Pyro', 'Spy', 'Engineer'];

/** Source's broad player hull lets names remain readable while aiming between
 * model triangles. Select the nearest living hull on either team, behind no
 * solid BSP brush, and use exact generation keys for scoreboard identities. */
export function aimedPlayer(scene, camera, visibility) {
    if (!scene?.entities) return null;
    const origin = camera.position.clone(), direction = camera.getWorldDirection(new T.Vector3());
    const ray = new T.Ray(origin, direction), box = new T.Box3(), hit = new T.Vector3();
    let best = null, distance = 8192;
    for (const [key, entity] of scene.entities) {
        const player = entity.state.components[Component.Player];
        if (!player || !(player.state & 1) || player.state & 64 || player.team < 2 || entity.object?.visible === false) continue;
        const position = entity.object?.getWorldPosition(new T.Vector3()) ?? new T.Vector3().fromArray(entity.state.components[Component.Transform].position);
        const scale = entity.state.components[Component.Appearance]?.scale ?? 1;
        box.min.copy(position).add(new T.Vector3(-24, -24, 0).multiplyScalar(scale));
        box.max.copy(position).add(new T.Vector3(24, 24, player.state & 4 ? 62 : 82).multiplyScalar(scale));
        if (box.containsPoint(origin) || !ray.intersectBox(box, hit)) continue;
        const candidateDistance = origin.distanceTo(hit);
        if (candidateDistance >= distance || visibility?.trace(origin.toArray(), hit.toArray()).fraction < 0.999) continue;
        distance = candidateDistance; best = { key, player };
    }
    return best;
}

export class LiveTargetId {
    constructor({ root, cache, players, diagnostic = () => {} }) {
        Object.assign(this, { root, cache, players, diagnostic });
        this.name = document.createElement('strong'); this.info = document.createElement('span');
        this.name.dataset.vguiControl = 'TargetNameLabel'; this.info.dataset.vguiControl = 'TargetDataLabel';
        this.health = document.createElement('span'); this.health.className = 'live-target-health';
        this.root.append(this.health, this.name, this.info); this.root.setAttribute('aria-label', 'Target player');
        this.root.setAttribute('role', 'status');
    }
    reset() { this.root.hidden = true; this.last = null; }
    async load(assetSet) {
        if (!assetSet || assetSet === this.assetSet) return;
        this.assetSet = assetSet; this.abort?.abort(); const abort = this.abort = new AbortController();
        this.root.style.width = ''; this.root.style.minHeight = ''; this.root.style.bottom = '';
        try {
            const entries = await readResource('resource/ui/targetid.res', path => this.cache.fetch(`/assets/${path}?assetSet=${encodeURIComponent(assetSet)}`, abort.signal));
            if (abort.signal.aborted) return;
            const panel = entries.find(entry => Array.isArray(entry.value))?.value;
            const props = resourceProperties(panel);
            const width = Math.max(180, Math.min(400, resourceCoordinate(props.wide, 640, 280)));
            const height = Math.max(44, Math.min(120, resourceCoordinate(props.tall, 480, 60)));
            this.root.style.width = `${width}px`; this.root.style.minHeight = `${height}px`;
            for (const [node, key] of [[this.name, 'targetnamelabel'], [this.info, 'targetdatalabel']]) {
                const label = resourceProperties(resourceValue(panel, key));
                const align = label.textalignment?.toLowerCase();
                if (['left', 'center', 'right'].includes(align)) node.style.textAlign = align;
            }
        } catch (error) {
            if (!abort.signal.aborted) this.diagnostic(`Target ID layout unavailable; using the default layout. ${error.message}`);
        }
    }
    frame(scene, camera, visibility) {
        const target = aimedPlayer(scene, camera, visibility);
        if (!target) { this.reset(); return; }
        const row = this.players.get(target.key), player = target.player;
        const health = player.health !== undefined && player.maxHealth ? `${player.health} / ${player.maxHealth}` : '';
        const identity = `${target.key}:${row?.name}:${player.team}:${player.playerClass}:${health}`;
        this.root.hidden = false;
        if (identity === this.last) return;
        this.last = identity;
        this.root.classList.toggle('live-target-red', player.team === 2);
        this.root.classList.toggle('live-target-blue', player.team === 3);
        this.name.textContent = row?.name ?? `Player ${target.key.split(':')[0]}`;
        this.info.textContent = classes[player.playerClass] ?? 'Player';
        this.health.textContent = health; this.health.hidden = !health;
        this.health.classList.toggle('live-target-hurt', player.health < player.maxHealth / 2);
        this.health.classList.toggle('live-target-overheal', player.health > player.maxHealth);
    }
    dispose() { this.abort?.abort(); this.reset(); this.root.replaceChildren(); }
}
